<template>
  <v-menu offset-y :max-width="48 * 3 + 'px'">
    <template v-slot:activator="{ on, attrs }">
      <div
        data-drag-handle
        style="max-width: 100%; width: 100%"
        v-bind="attrs"
        v-on="on"
      >
        <div style="width: fit-content; position: relative; max-width: 100%">
          <v-img
            v-if="imageMode === 'full-width'"
            width="100%"
            max-width="100%"
            height="auto"
            :src="imageSrc"
            :class="{ selected }"
            :contain="true"
            @load="setImgLoaded"
          />
          <img
            v-else-if="imageMode === 'max-height'"
            style="max-width: 100%; width: auto"
            :height="imageSize"
            :src="imageSrc"
            :class="{ selected }"
            @load="setImgLoaded"
          />
          <v-img
            v-else-if="imageMode === 'width'"
            max-width="100%"
            :width="imageSize"
            height="auto"
            :src="imageSrc"
            :class="{ selected }"
            :contain="true"
            @load="setImgLoaded"
          />
          <v-img
            v-else-if="imageMode === 'height'"
            max-width="100%"
            width="auto"
            :height="imageSize"
            :src="imageSrc"
            :class="{ selected }"
            :contain="true"
            @load="setImgLoaded"
          />
          <div
            v-if="imgLoaded"
            style="
              position: absolute;
              bottom: 0;
              background: #0a0a0a99;
              margin-left: auto;
              margin-right: auto;
              left: 0;
              right: 0;
              color: #ffffff !important;
              display: flex;
              justify-content: center;
              padding: 4px;
            "
          >
            <div
              style="word-break: break-all"
              @click.stop="startEditing"
              v-if="!nameIsEdited"
              class="file-name-overlay"
            >
              {{ editedFileName }}
            </div>
            <div class="d-flex" v-if="nameIsEdited">
              <v-text-field
                dark
                color="white"
                :rules="[nameRule]"
                hide-details="true"
                class="ml-6 mr-0 pt-0"
                style="font-size: 18px; color: white"
                v-model="editedFileName"
                autofocus
                @click.stop
                variant="underlined"
              ></v-text-field>
              <v-icon
                v-tooltip="{ content: 'Speichern', offset: 10 }"
                @click.stop="emitEditAndStopEditing"
                color="white"
                class="pointer"
                >{{ mdiCheck }}
              </v-icon>
              <v-icon
                v-tooltip="{ content: 'Abbrechen', offset: 10 }"
                color="white"
                @click.stop="closeEdit"
                class="pointer"
                >{{ mdiClose }}
              </v-icon>
            </div>
          </div>
        </div>
      </div>
    </template>
    <div
      style="
        background: white;
        overflow: hidden;
        display: flex;
        align-items: center;
      "
    >
      <image-mode-menu-item
        @click="changeMode('max-height')"
        :selected="imageMode === 'max-height'"
        tooltip="Feste Höhe"
      >
        <template v-slot="slotProps">
          <width-normal-icon :fill="slotProps.fill" />
        </template>
      </image-mode-menu-item>
      <image-mode-menu-item
        @click="changeMode('full-width')"
        :selected="imageMode === 'full-width'"
        tooltip="Volle Breite"
      >
        <template v-slot="slotProps">
          <width-full-icon :fill="slotProps.fill" />
        </template>
      </image-mode-menu-item>
      <image-mode-menu-item
        @click="changeMode('attachment')"
        :selected="imageMode === 'attachment'"
        tooltip="Als Dateianhang anzeigen"
      >
        <template v-slot="slotProps">
          <attachment-icon :fill="slotProps.fill" />
        </template>
      </image-mode-menu-item>
    </div>
  </v-menu>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, toRef, watch } from "vue"
import WidthNormalIcon from "components/Wysiwyg/extensions/attachment/attachment-item/WidthNormalIcon.vue"
import WidthFullIcon from "components/Wysiwyg/extensions/attachment/attachment-item/WidthFullIcon.vue"
import AttachmentIcon from "components/Wysiwyg/extensions/attachment/attachment-item/AttachmentIcon.vue"
import ImageModeMenuItem from "components/Wysiwyg/extensions/attachment/attachment-item/ImageModeMenuItem.vue"
import { mdiCheck, mdiClose } from "@mdi/js"
import { ImageMode } from "components/Wysiwyg/extensions/attachment/attachment-item/AttachmentItemNodeView.vue"

export default defineComponent({
  name: "AttachmentItemImageMode",
  components: {
    ImageModeMenuItem,
    AttachmentIcon,
    WidthFullIcon,
    WidthNormalIcon,
  },
  props: {
    fileName: {
      type: String,
      required: true,
    },
    imageSrc: {
      type: String,
      required: true,
    },
    imageSize: {
      type: Number,
      required: false,
    },
    imageMode: {
      type: String as PropType<ImageMode>,
      required: true,
    },
    selected: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, { emit }) {
    const nameIsEdited = ref(false)

    const startEditing = () => {
      nameIsEdited.value = true
    }

    const nameRule = (v) => !!v || "Name wird benötigt."

    const editedFileName = ref(props.fileName)

    const closeEdit = () => {
      nameIsEdited.value = false
      editedFileName.value = props.fileName
    }

    const emitEditAndStopEditing = () => {
      if (!editedFileName.value) {
        closeEdit()
        return
      }
      emit("fileNameChange", editedFileName.value)
      nameIsEdited.value = false
    }

    const changeMode = (mode: ImageMode) => {
      emit("updateDisplayMode", mode)
      if (mode === "max-height") {
        emit("updateSize", "350")
      } else {
        emit("updateSize", undefined)
      }
    }

    const imgLoaded = ref(false)
    const setImgLoaded = () => {
      console.log("loaded")
      imgLoaded.value = true
    }

    // This is a fix for a Firefox Bug where text is not selectable if the wrapping element is draggable.
    // TBH I don't know how this REALLY works. Even if draggable is set to false, the image is still draggable.
    // But it seems to work. If i remove this watch, it won't work anymore.
    watch(
      () => nameIsEdited.value,
      (v) => {
        if (v) {
          const selectedNode = document.querySelector(
            ".ProseMirror-selectednode"
          )
          if (selectedNode) {
            selectedNode.removeAttribute("draggable")
          }
        } else {
          const selectedNode = document.querySelector(
            ".ProseMirror-selectednode"
          )
          if (selectedNode) {
            selectedNode.setAttribute("draggable", "true")
          }
        }
      }
    )

    return {
      imageMode: toRef(props, "imageMode"),
      changeMode,
      nameIsEdited,
      startEditing,
      mdiCheck,
      mdiClose,
      nameRule,
      editedFileName,
      emitEditAndStopEditing,
      closeEdit,
      imgLoaded,
      setImgLoaded,
    }
  },
})
</script>

<style scoped>
.selected {
  border: 2px solid #307ef7;
}

.file-name-overlay::selection {
  color: white !important;
}
</style>
